import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { setlistsApi } from "../services/setlists";
import type { ViewResponse } from "../types/setlists";
import { useScrollLockHeal } from "../hooks/useScrollLockHeal";
import { openExternal } from "../lib/openExternal";
import { prepareMusicKit, addSetlistToAppleMusic, AppleAddError } from "../lib/appleMusicAdd";
import "./PlaylistPage.css";
import { t as tr } from "../i18n";

// Public fan page for a purchased setlist — /playlist/:receiptId (the
// receiptId IS the access token, same convention as /receipt/:id). Three
// graceful states from GET /api/setlists/view/:receiptId:
//   delivered → the set: 4-art mosaic, name, meta, Spotify hero (only when
//               the app-account playlist exists), "Add to Apple Music" hero
//               (only when tracks carry server-resolved Apple catalog ids
//               AND the MusicKit developer token is live — fan authorizes
//               their OWN Apple Music account, playlist lands in THEIR
//               library), track rows deep-linking to open.spotify.com
//   pending   → friendly "waiting for the DJ to post the set" (auto-polls,
//               so the page flips to the playlist the moment it's delivered)
//   closed    → the order closed before delivery — the fan was never charged
//               (declined / hold expired / cancelled all release the auth).

// Brand marks for the two hero buttons (founder pick: option B — both
// buttons full weight, each with its platform logo). Exported: the Buy
// Playlist purchase sheet renders the same pair (founder pick A).
export const SpotifyMark: React.FC = () => (
  <svg viewBox="0 0 24 24" aria-hidden focusable="false">
    <path
      fill="currentColor"
      d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"
    />
  </svg>
);

export const AppleMark: React.FC = () => (
  <svg viewBox="0 0 24 24" aria-hidden focusable="false">
    <path
      fill="currentColor"
      d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701"
    />
  </svg>
);

// "9:54 pm" out of "8/15/26, 9:54:25 PM PDT" (narrow no-break space safe).
function clockOf(raw?: string | null): string | null {
  if (!raw) return null;
  const m = String(raw)
    .replace(/ /g, " ")
    .match(/(\d{1,2}):(\d{2}):\d{2}\s*(AM|PM)/i);
  if (!m) return null;
  return `${m[1]}:${m[2]} ${m[3].toLowerCase()}`;
}

const PlaylistPage: React.FC = () => {
  // Silent heal for stranded sheet scroll-locks.
  useScrollLockHeal();
  const { receiptId } = useParams<{ receiptId: string }>();
  const [view, setView] = useState<ViewResponse | null>(null);
  const [failed, setFailed] = useState<"notfound" | "error" | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ── "Add to Apple Music" ──
  // none   → hidden (no resolved ids, server key not live, or MusicKit
  //          couldn't start — the page just keeps its other buttons)
  // ready  → MusicKit configured; a tap goes straight to Apple's popup
  // adding → in flight
  // done   → the playlist is in the fan's own library
  const [appleState, setAppleState] = useState<"none" | "ready" | "adding" | "done">("none");
  const [appleNote, setAppleNote] = useState<string | null>(null);
  const musicRef = useRef<any>(null);
  const appleTokenRef = useRef<string | null>(null);
  const applePreparedRef = useRef(false);

  const fetchView = React.useCallback(() => {
    if (!receiptId) return;
    setlistsApi
      .view(receiptId)
      .then((res) => {
        setView(res);
        setFailed(null);
      })
      .catch((err) => {
        if (err?.response?.status === 404) setFailed("notfound");
        else setFailed("error");
      });
  }, [receiptId]);

  useEffect(() => {
    fetchView();
  }, [fetchView]);

  // Waiting fans get the playlist the moment it lands — poll quietly.
  useEffect(() => {
    if (view?.state !== "pending") {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
      return;
    }
    pollRef.current = setInterval(() => {
      if (!document.hidden) fetchView();
    }, 15000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [view?.state, fetchView]);

  useEffect(() => {
    if (view?.state === "delivered" && view.name) {
      document.title = tr("playlistPage.requestApp", { name: view.name, djName: view.djName });
    }
  }, [view?.state, view?.name, view?.djName]);

  const mosaic = useMemo(
    () =>
      (view?.tracks || [])
        .map((t) => t.image)
        .filter(Boolean)
        .slice(0, 4) as string[],
    [view?.tracks]
  );

  const timesLine = useMemo(() => {
    const a = clockOf(view?.session?.startedAt);
    const b = clockOf(view?.session?.endedAt);
    return a && b ? `${a} – ${b}` : null;
  }, [view?.session]);

  const appleIds = useMemo(
    () => (view?.tracks || []).map((t) => t.appleMusicId).filter(Boolean) as string[],
    [view?.tracks]
  );

  // Configure MusicKit up front (this page only, and only when the setlist
  // actually has resolved Apple ids) so the tap handler can open Apple's
  // popup inside the user gesture — iPhone Safari blocks popups that follow
  // an await chain. Any failure here = the button never appears.
  useEffect(() => {
    if (view?.state !== "delivered" || appleIds.length === 0 || applePreparedRef.current) return;
    applePreparedRef.current = true;
    setlistsApi
      .musicKitToken()
      .then(async (res) => {
        appleTokenRef.current = res.token;
        musicRef.current = await prepareMusicKit(res.token);
        setAppleState("ready");
      })
      .catch(() => {
        // 404 = the Apple key isn't configured server-side; anything else =
        // MusicKit couldn't start. Either way: hidden button, working page.
      });
  }, [view?.state, appleIds.length]);

  const addToAppleMusic = () => {
    if (appleState !== "ready" || !musicRef.current || !appleTokenRef.current || !view) return;
    setAppleState("adding");
    setAppleNote(null);
    addSetlistToAppleMusic({
      music: musicRef.current,
      developerToken: appleTokenRef.current,
      // view.name IS the server-composed display name ("DJ Jax'on - August
      // 15 Setlist") — the same string as the doc name and the Spotify
      // playlist title. Never append the DJ: it's already in there.
      name: String(view.name || "").slice(0, 100),
      // Description carries no brand (platform-terms review):
      // Spotify's guidelines frown on promoting commercial services inside
      // the app, and the DJ is already credited in the playlist name.
      description: "The set as it was played.",
      appleMusicIds: appleIds,
    })
      .then(() => setAppleState("done"))
      .catch((err) => {
        setAppleState("ready");
        const code = err instanceof AppleAddError ? err.code : "failed";
        setAppleNote(
          code === "declined"
            ? tr("playlistPage.nothingWasAdded")
            : code === "no-subscription"
            ? tr("playlistPage.thisNeedsAnApple")
            : tr("playlistPage.couldnTAddThe")
        );
      });
  };

  if (failed) {
    return (
      <div className="pl-page">
        <div className="pl-state-card">
          <div className="pl-state-emoji" aria-hidden>
            {failed === "notfound" ? "🔎" : "🎧"}
          </div>
          <h1 className="pl-state-title">
            {failed === "notfound" ? tr("playlistPage.thisLinkDoesnT") : tr("playlistPage.oneSec")}
          </h1>
          <p className="pl-state-sub">
            {failed === "notfound"
              ? tr("playlistPage.checkTheLinkFrom")
              : tr("playlistPage.weCouldnTLoad")}
          </p>
          {failed === "error" && (
            <button type="button" className="pl-hero-btn" onClick={fetchView}>
              {tr("playlistPage.tryAgain")}
            </button>
          )}
        </div>
      </div>
    );
  }

  if (!view) {
    return (
      <div className="pl-page">
        <div className="pl-loading" aria-label={tr("playlistPage.loading")}>
          <span className="pl-spinner" aria-hidden />
        </div>
      </div>
    );
  }

  if (view.state === "pending") {
    return (
      <div className="pl-page">
        <div className="pl-state-card">
          <div className="pl-state-emoji" aria-hidden>
            🎧
          </div>
          <h1 className="pl-state-title">{tr("playlistPage.theSetIsComing")}</h1>
          <p className="pl-state-sub">
            {tr("playlistPage.waitingForToPost", { djName: view.djName })}
          </p>
          <p className="pl-state-note">{tr("playlistPage.youReOnlyCharged")}</p>
        </div>
      </div>
    );
  }

  if (view.state === "closed") {
    return (
      <div className="pl-page">
        <div className="pl-state-card">
          <div className="pl-state-emoji" aria-hidden>
            🙏
          </div>
          <h1 className="pl-state-title">{tr("playlistPage.thisOrderWasClosed")}</h1>
          <p className="pl-state-sub">
            {tr("playlistPage.sSetDidnT", { djName: view.djName })}
          </p>
        </div>
      </div>
    );
  }

  // ── delivered ──
  const tracks = view.tracks || [];

  return (
    <div className="pl-page">
      <div className="pl-hero">
        {mosaic.length > 0 && (
          <div className={`pl-mosaic${mosaic.length < 4 ? " pl-mosaic--few" : ""}`}>
            {mosaic.map((src, i) => (
              <img key={i} src={src} alt="" loading="lazy" />
            ))}
          </div>
        )}
        <h1 className="pl-name">{view.name}</h1>
        <div className="pl-meta">
          {timesLine ? tr("playlistPage.metaSongsTimes", { djName: view.djName, count: tracks.length, times: timesLine }) : tr("playlistPage.metaSongs", { djName: view.djName, count: tracks.length })}
        </div>

        {view.spotifyPlaylistUrl && (
          <button
            type="button"
            className="pl-hero-btn"
            onClick={() => openExternal(view.spotifyPlaylistUrl!)}
          >
            <SpotifyMark />
            {tr("playlistPage.addToSpotify")}
          </button>
        )}
        {appleState === "done" ? (
          <div className="pl-apple-btn pl-apple-btn--done">
            <AppleMark />
            {tr("playlistPage.inYourAppleMusic")}
          </div>
        ) : appleState !== "none" ? (
          <button
            type="button"
            className="pl-apple-btn"
            disabled={appleState === "adding"}
            onClick={addToAppleMusic}
          >
            <AppleMark />
            {appleState === "adding" ? tr("playlistPage.adding") : tr("playlistPage.addToAppleMusic")}
          </button>
        ) : null}
        {appleNote && <div className="pl-apple-note">{appleNote}</div>}
      </div>

      <div className="pl-tracks">
        {tracks.map((t) =>
          t.spotifyId ? (
            <a
              key={`${t.order}-${t.spotifyId}`}
              className="pl-trk"
              href={`https://open.spotify.com/track/${t.spotifyId}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t.image ? (
                <img src={t.image} alt="" loading="lazy" />
              ) : (
                <span className="pl-trk-art-fallback" aria-hidden>
                  🎵
                </span>
              )}
              <span className="pl-trk-meta">
                <span className="pl-trk-t">{t.title}</span>
                <span className="pl-trk-a">{t.artist}</span>
              </span>
              {clockOf(t.startTime) && <span className="pl-trk-time">{clockOf(t.startTime)}</span>}
            </a>
          ) : (
            <div key={`${t.order}-txt`} className="pl-trk pl-trk--plain">
              <span className="pl-trk-art-fallback" aria-hidden>
                🎵
              </span>
              <span className="pl-trk-meta">
                <span className="pl-trk-t">{t.title}</span>
                <span className="pl-trk-a">{t.artist}</span>
              </span>
              {clockOf(t.startTime) && <span className="pl-trk-time">{clockOf(t.startTime)}</span>}
            </div>
          )
        )}
      </div>

      <div className="pl-foot">{tr("playlistPage.deliveredByRequestApp")}</div>
    </div>
  );
};

export default PlaylistPage;
